import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/* JWT now lives in an httpOnly `cm_session` cookie set by the backend on
   /auth/login + /auth/register. `withCredentials: true` ensures the cookie
   is sent on every request. We no longer read or write any token to
   localStorage — that removes the XSS exfiltration path entirely. */
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

/* Owner View Mode wiring.
 *
 * The owner account can toggle "View as Customer" to experience the exact
 * paying-customer flow. ViewModeContext calls setApiViewMode() whenever
 * the toggle flips; the interceptor below stamps every outgoing request
 * with `X-View-Mode: customer` so the backend treats that request as a
 * non-owner. In owner mode (default), no header is sent and non-owner
 * users are completely unaffected. */
let _currentViewMode = "owner";
if (typeof window !== "undefined" && window.localStorage) {
  _currentViewMode = window.localStorage.getItem("cm_view_mode") || "owner";
}

export function setApiViewMode(mode) {
  _currentViewMode = mode === "customer" ? "customer" : "owner";
}

api.interceptors.request.use((config) => {
  if (_currentViewMode === "customer") {
    config.headers = { ...(config.headers || {}), "X-View-Mode": "customer" };
  }
  return config;
});

/* Global 401 handler.
 *
 * If any API call returns 401 the user's session is invalid (cookie missing,
 * JWT expired, browser blocked cookie, etc). We must never leave the user
 * stuck on the current page seeing "Not authenticated" — that's the exact
 * homeowner freeze bug fixed 2026-07-07.
 *
 * On 401 we:
 *   1. Clear any local user state (via the invalidator hook wired by AuthContext).
 *   2. Redirect to /login?from=<current-path>&reason=session-expired, so the
 *      user lands on the sign-in screen with a friendly banner and returns
 *      to what they were doing after signing in.
 *
 * Excluded paths (401 there is expected / handled locally):
 *   - /auth/me       — the initial "am I logged in?" check
 *   - /auth/login    — wrong password / disabled account
 *   - /auth/register — validation error path
 *   - /auth/forgot-password, /auth/reset-password — public flows
 *   - Anything called from the /login or /register pages themselves.
 */
let _onAuthExpired = null;
export function setAuthExpiredHandler(fn) {
  _onAuthExpired = typeof fn === "function" ? fn : null;
}

const AUTH_EXCLUDED_PATHS = [
  "/auth/me",
  "/auth/login",
  "/auth/register",
  "/auth/forgot-password",
  "/auth/reset-password",
];

function _isExcluded(url) {
  if (!url) return false;
  return AUTH_EXCLUDED_PATHS.some((p) => url.includes(p));
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const cfg = error?.config || {};
    const url = cfg.url || "";
    if (status === 401 && !_isExcluded(url)) {
      // Guard: only fire once per navigation to avoid loops.
      if (typeof window !== "undefined") {
        const path = window.location.pathname;
        // Do not bounce when we're already on the login/register pages
        // (their own auth calls fail with 401 all the time as part of the
        // normal "not logged in yet" state).
        if (path !== "/login" && path !== "/register") {
          // Fire the redirect FIRST so the /login URL carries our query
          // params. Clearing local auth state after is fine because
          // window.location.replace triggers a hard reload and unmounts
          // the React tree anyway — this avoids a brief "no banner"
          // flash caused by <Protected> navigating first.
          const from = encodeURIComponent(path + (window.location.search || ""));
          window.location.replace(`/login?from=${from}&reason=session-expired`);
          if (_onAuthExpired) {
            try { _onAuthExpired(); } catch { /* ignore */ }
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return friendly(detail);
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? friendly(e.msg) : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return friendly(detail.msg);
  return String(detail);
}

// Map raw backend / Pydantic validation messages to homeowner-friendly copy.
// Keep this list short and specific — over-mapping hides real signals.
function friendly(raw) {
  const s = String(raw || "").toLowerCase();
  if (s.includes("value is not a valid email address") || s.includes("email address")) {
    return "Please enter a valid email address.";
  }
  if (s.includes("ensure this value has at least") && s.includes("password")) {
    return "Password must be at least 6 characters.";
  }
  if (s.includes("already registered") || s.includes("already exists")) {
    return "An account with that email already exists. Try signing in instead.";
  }
  return raw;
}

export default api;
